import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { useCalculateShippingRatesMutation } from '../redux/apis/endpoints/deliveryApi';
import { removeUnshippableItems, clearUnshippableItems } from '../redux/slices/checkoutSlice';
import { getComparableDayValue } from '../components/checkout/delivery/utils/shippingUtils';

// Helper: Calculate total weight for all items in this group
const calculateParcelWeightInternal = (items) => {
  let hasWeightError = false;
  const totalWeight = items.reduce((currentTotalWeight, item) => {
    let weight = null;
    let unit = 'kg'; // Default unit

    if (item.parcelInfo?.weight) {
      weight = parseFloat(item.parcelInfo.weight);
      unit = item.parcelInfo.units?.mass || 'kg';
    } else if (item.product?.parcelInfo?.weight) {
      weight = parseFloat(item.product.parcelInfo.weight);
      unit = item.product.parcelInfo.units?.mass || 'kg';
    }

    if (weight === null || isNaN(weight) || weight <= 0) {
      hasWeightError = true;
      return currentTotalWeight;
    }

    if (unit === 'lb') {
      weight *= 0.453592; // lb to kg
    } else if (unit === 'oz') {
      weight *= 0.0283495; // oz to kg
    }
    return currentTotalWeight + (weight * item.quantity);
  }, 0);

  if (hasWeightError) {
    throw new Error('Missing weight information for one or more items.');
  }
  return totalWeight;
};

export const useShippingRateCalculator = ({
  customerAddress, // The TO address object (or FROM for returns)
  merchantAddress, // The FROM address object (or TO for returns)
  items,           // Array of items for this shipment group
  addressId,       // ID for this shipment group (for Redux updates)
  currency,        // Add currency prop
  onCalculationStart,
  onRatesSuccess,
  onRatesUnshippable,
  onRatesError,
  isReturn = false, // Flag to indicate if this is a return shipment
}) => {
  // console.log('[useShippingRateCalculator] Hook received items:', JSON.stringify(items, null, 2)); // Remove log
  const dispatch = useDispatch();
  const [calculateShippingRatesApi, { isLoading: ratesApiLoading }] = useCalculateShippingRatesMutation();

  const [internalLoading, setInternalLoading] = useState(false);
  const [displayableRatesState, setDisplayableRatesState] = useState([]);
  const [rawRatesState, setRawRatesState] = useState([]);
  const [errorState, setErrorState] = useState(null);

  const previousCustomerAddressIdRef = useRef(null);
  const lastProcessedPropsStringRef = useRef(null);

  const onCalculationStartRef = useRef(onCalculationStart);
  const onRatesSuccessRef = useRef(onRatesSuccess);
  const onRatesUnshippableRef = useRef(onRatesUnshippable);
  const onRatesErrorRef = useRef(onRatesError);

  useEffect(() => { onCalculationStartRef.current = onCalculationStart; }, [onCalculationStart]);
  useEffect(() => { onRatesSuccessRef.current = onRatesSuccess; }, [onRatesSuccess]);
  useEffect(() => { onRatesUnshippableRef.current = onRatesUnshippable; }, [onRatesUnshippable]);
  useEffect(() => { onRatesErrorRef.current = onRatesError; }, [onRatesError]);

  const fetchLogic = useCallback(async (currentCustomerAddress, currentItems, currentCurrency) => {
    if (!currentCustomerAddress || !currentItems || currentItems.length === 0) {
      onRatesErrorRef.current?.('Missing address or items for rate calculation.');
      return;
    }
    setInternalLoading(true);
    setErrorState(null);
    onCalculationStartRef.current?.();
    
    let fromAddressId;
    let toAddressId;
    
    try {
      // IMPORTANT: For both normal shipping and returns, we use the same address ordering:
      // fromAddressId = merchant, toAddressId = customer
      // The backend will swap these internally for return shipments based on the isReturn flag
      
      // Get merchant address (FROM)
      if (isReturn && merchantAddress) {
        // Use the provided merchant address for returns
        if (typeof merchantAddress === 'object') {
          // If it's an object, extract the ID or use the whole object
          if (merchantAddress.id) {
            fromAddressId = merchantAddress.id;
            console.log('[useShippingRateCalculator] Using merchant address ID for return:', fromAddressId);
          } else {
            // No ID, use the whole object as fromAddress in the API call
            fromAddressId = null; // Will be handled later for full address
            console.log('[useShippingRateCalculator] Using complete merchant address object for return');
          }
        } else {
          // If it's a primitive (ID), use it directly
          fromAddressId = merchantAddress;
          console.log('[useShippingRateCalculator] Using direct merchant address ID for return:', fromAddressId);
        }
      } else {
        // Normal case: get merchant address from items
      const fromAddressData = currentItems[0]?.shippingAddress || currentItems[0]?.product?.shippingAddress || currentItems[0]?.merchant?.shippingAddress;
      if (!fromAddressData) { throw new Error('No merchant address available.'); }
      if (typeof fromAddressData === 'number') { fromAddressId = fromAddressData; }
      else if (fromAddressData.id) { fromAddressId = fromAddressData.id; }
      else if (fromAddressData.hasOwnProperty('__component')) { fromAddressId = fromAddressData.id || fromAddressData._id; }
      else { fromAddressId = fromAddressData; }
      }
      
      // Customer address (TO)
      toAddressId = currentCustomerAddress.id;
      
      console.log('[useShippingRateCalculator] Using addresses:', {
        fromAddressId, // Merchant address ID
        toAddressId,   // Customer address ID
        isReturn,      // Backend will handle swapping for returns
        usingFullMerchantAddressObject: isReturn && !fromAddressId && merchantAddress && typeof merchantAddress === 'object'
      });
      
      // Only validate fromAddressId if we're not using a full address object
      if (!fromAddressId && !(isReturn && merchantAddress && typeof merchantAddress === 'object')) {
        throw new Error('Could not determine from address ID.');
      }
      if (!toAddressId) { throw new Error('Could not determine to address ID.'); }
    } catch (addrErr) {
      onRatesErrorRef.current?.(`Unable to calculate shipping rates: ${addrErr.message}`);
      setInternalLoading(false);
      return;
    }
    
    let parcels = []; // Initialize as an empty array
    try {
      // Iterate over all items to create an array of parcel details
      // Each parcel object will contain all available dimension and weight info.
      // The backend will decide if aggregation to weight-only is needed for multiple parcels.
      for (const item of currentItems) {
        const parcelInfo = item?.parcelInfo || item?.product?.parcelInfo;

        // Validate essential information: weight and units must exist and be valid.
        if (!parcelInfo || parcelInfo.weight == null || !parcelInfo.units?.mass || 
            parcelInfo.length == null || parcelInfo.width == null || parcelInfo.height == null || !parcelInfo.units?.distance) {
          console.error('Missing complete parcel information (dimensions, weight, or units) for item:', item?.id || 'unknown ID', parcelInfo);
          throw new Error('Product is missing required parcel information (dimensions, weight, or units).');
        }

        const weight = parseFloat(parcelInfo.weight);
        const length = parseFloat(parcelInfo.length);
        const width = parseFloat(parcelInfo.width);
        const height = parseFloat(parcelInfo.height);

        if (isNaN(weight) || weight <= 0 || isNaN(length) || length <= 0 || isNaN(width) || width <= 0 || isNaN(height) || height <= 0) {
          throw new Error('Invalid non-positive value found in parcel dimensions or weight for an item.');
        }

        const currentParcel = {
          weight: String(weight),
          mass_unit: parcelInfo.units.mass,
          length: String(length),
          width: String(width),
          height: String(height),
          distance_unit: parcelInfo.units.distance,
        };
        parcels.push(currentParcel);
      }

      if (parcels.length === 0) {
        throw new Error('No valid parcel data could be constructed.');
      }
      
      console.log(`[useShippingRateCalculator] Constructed ${parcels.length} full parcel objects for API.`);

    } catch (parcelErr) {
      const itemIds = currentItems.map(item => item.id || item.cartItemIndex);
      const errorMessage = parcelErr.message || 'Missing product parcel information.';
      onRatesErrorRef.current?.(errorMessage);
      dispatch(removeUnshippableItems({ method: 'shipping', addressId, items: itemIds })); 
      setInternalLoading(false);
      return;
    }

    try {
      // Keep this useful log
      console.log('[useShippingRateCalculator] Parcels being sent to API mutation:', JSON.stringify(parcels, null, 2)); 

      // Get merchant info from items or from merchantAddress for returns
      let merchantUsername = null;
      let merchantFirstname = null;
      let merchantLastname = null;
      
      if (isReturn && merchantAddress) {
        // For returns, try to get merchant info from the merchantAddress object
        merchantUsername = merchantAddress.username || merchantAddress.name || 'ReturnMerchant';
        merchantFirstname = merchantAddress.firstname || '';
        merchantLastname = merchantAddress.lastname || '';
        console.log('[useShippingRateCalculator] Using merchant info from merchantAddress for return shipment:', 
          { username: merchantUsername, firstname: merchantFirstname, lastname: merchantLastname });
      } else {
        // Normal flow - get merchant info from items
      const merchantInfo = currentItems[0]?.merchant;
        merchantUsername = merchantInfo?.username;
        merchantFirstname = merchantInfo?.firstname;
        merchantLastname = merchantInfo?.lastname;
      }
      
      if (!merchantUsername && !isReturn) {
          console.error("[useShippingRateCalculator] Could not determine Merchant Username from items for rate calculation. Item structure issue?");
          onRatesErrorRef.current?.("Merchant information is missing to calculate rates.");
          setInternalLoading(false);
          return; // Stop if no merchant username for normal shipping
      }
      
      // For returns, use a default username if none available
      if (!merchantUsername && isReturn) {
        merchantUsername = 'ReturnMerchant';
        console.log('[useShippingRateCalculator] Using default merchant username for return shipping');
      }

      // Log the address IDs to help troubleshoot
      console.log('[useShippingRateCalculator] SENDING API CALL with parameters:', {
        fromAddressId,
        toAddressId,
        merchantUsername,
        isReturn,
        fromCustomer: isReturn // This should be true if isReturn is true
      });

      const ratesResponse = await calculateShippingRatesApi({
           fromAddressId,
           toAddressId,
           parcels,
           merchantUsername: merchantUsername,
           merchantFirstname: merchantFirstname || '',
           merchantLastname: merchantLastname || '',
           currency: currentCurrency,
           isReturn: isReturn === true, // Ensure this is explicitly boolean true
           fromCustomer: isReturn === true,
           // If this is a return and we don't have a fromAddressId but do have a merchantAddress object,
           // pass the full address object
           ...(isReturn && !fromAddressId && merchantAddress && typeof merchantAddress === 'object' ? {
               fromAddress: merchantAddress
           } : {})
       }).unwrap();

      console.log('[useShippingRateCalculator] API response:', ratesResponse);

      if (ratesResponse.unshippable) {
        const itemIds = currentItems.map(item => item.id || item.cartItemIndex);
        onRatesUnshippableRef.current?.(itemIds, ratesResponse.error || 'This product cannot be shipped to your address.');
        dispatch(removeUnshippableItems({ method: 'shipping', addressId, items: itemIds }));
        setDisplayableRatesState([]);
        setRawRatesState([]);
      } else {
        // Get rates from the transformed response
        const allApiRates = ratesResponse.rates || [];

        // +++ Filter rates by checkout currency +++
        const currencyMatchedApiRates = allApiRates.filter(rate => (rate.currency || 'USD').toUpperCase() === currentCurrency.toUpperCase());

        if (currencyMatchedApiRates.length === 0 && allApiRates.length > 0) {
          // Rates were returned, but none matched the target currency
          const itemIds = currentItems.map(item => item.id || item.cartItemIndex);
          const currencyErrorMessage = `No shipping rates available for ${currentCurrency}. Rates found in other currencies.`;
          onRatesErrorRef.current?.(currencyErrorMessage); // Or onRatesUnshippableRef if preferred
          // dispatch(removeUnshippableItems({ method: 'shipping', addressId, items: itemIds })); // Decide if items become 'unshippable'
          setDisplayableRatesState([]);
          setRawRatesState([]);
          setInternalLoading(false);
          return; // Stop further processing
        } else if (currencyMatchedApiRates.length === 0 && allApiRates.length === 0) {
          // No rates returned at all
          const itemIds = currentItems.map(item => item.id || item.cartItemIndex);
          onRatesUnshippableRef.current?.(itemIds, ratesResponse.error || 'No shipping rates found for your address.');
          dispatch(removeUnshippableItems({ method: 'shipping', addressId, items: itemIds }));
          setDisplayableRatesState([]);
          setRawRatesState([]);
          setInternalLoading(false);
          return; // Stop further processing
        }
        // +++++++++++++++++++++++++++++++++++++++

        const formattedForDisplay = currencyMatchedApiRates.map(rate => ({
          id: rate.id || rate.object_id,
          service: rate.serviceName || rate.servicelevel?.name || 'Shipping',
          carrier: rate.provider || rate.carrier_name || rate.carrier || 'Unknown Carrier',
          days: rate.estimated_days || 'N/A',
          price: parseFloat(rate.amount) || 0,
          currency: rate.currency || 'USD'
        }));

        let finalDisplayableRates = [];
        if (formattedForDisplay.length > 0) {
          const sortedByPrice = [...formattedForDisplay].sort((a, b) => a.price - b.price);
          const cheapestRate = sortedByPrice[0];
          finalDisplayableRates.push(cheapestRate);

          if (formattedForDisplay.length > 1) {
            let secondRateToAdd = null;
            const sortedByDays = [...formattedForDisplay].sort((a, b) => getComparableDayValue(a.days) - getComparableDayValue(b.days));
            const fastestRateCandidate = sortedByDays.find(rate => rate.id !== cheapestRate.id);

            if (fastestRateCandidate) {
              secondRateToAdd = fastestRateCandidate;
            } else if (sortedByPrice.length > 1 && sortedByPrice[1].id !== cheapestRate.id) {
              secondRateToAdd = sortedByPrice[1];
            }
            
            if (secondRateToAdd) {
              finalDisplayableRates.push(secondRateToAdd);
            }
          }
          
          finalDisplayableRates = Array.from(new Map(finalDisplayableRates.map(rate => [rate.id, rate])).values())
                                       .sort((a,b) => a.price - b.price);
        }
        
        dispatch(clearUnshippableItems({ method: 'shipping', addressId }));
        setDisplayableRatesState(finalDisplayableRates);
        setRawRatesState(currencyMatchedApiRates);
        onRatesSuccessRef.current?.(finalDisplayableRates, currencyMatchedApiRates);
      }
    } catch (apiErr) {
      onRatesErrorRef.current?.(apiErr.data?.message || apiErr.message || 'Unable to fetch shipping rates. Please try again.');
      setDisplayableRatesState([]);
      setRawRatesState([]);
    } finally {
      setInternalLoading(false);
    }
  }, [addressId, calculateShippingRatesApi, dispatch, isReturn, merchantAddress]);

  const debouncedFetchLogic = useCallback(debounce(fetchLogic, 300), [fetchLogic]);
  
  // Add a ref to track in-flight requests
  const inflightRequestRef = useRef(false);

  useEffect(() => {
    if (internalLoading || ratesApiLoading || inflightRequestRef.current) {
      return; 
    }
    if (customerAddress && items && items.length > 0 && currency) {
      const currentAddressId = customerAddress.id;
      const itemsSignature = JSON.stringify(items.map(item => ({ 
        id: item.id || item.cartItemIndex, 
        quantity: item.quantity,
        productId: item.product?.id 
      })));
      const currentPropsString = `${currentAddressId}_${itemsSignature}_${currency}_${isReturn}`;
      if (lastProcessedPropsStringRef.current !== currentPropsString) {
        if (previousCustomerAddressIdRef.current !== currentAddressId) {
          setDisplayableRatesState([]);
          setRawRatesState([]);
          setErrorState(null);
          previousCustomerAddressIdRef.current = currentAddressId;
        }
        
        // Set inflight request flag to true before making the request
        inflightRequestRef.current = true;
        
        // Debounced functions don't return promises, so we can't use .finally()
        // Instead, we'll rely on the setInternalLoading(false) in fetchLogic's finally block
        debouncedFetchLogic(customerAddress, items, currency);
        
        lastProcessedPropsStringRef.current = currentPropsString;
      }
    } else {
      setDisplayableRatesState([]);
      setRawRatesState([]);
      setErrorState(null);
      previousCustomerAddressIdRef.current = null;
      lastProcessedPropsStringRef.current = null;
    }
  }, [customerAddress, items, debouncedFetchLogic, internalLoading, ratesApiLoading, currency, isReturn]);

  // Reset the inflightRequestRef when internal loading changes to false
  useEffect(() => {
    if (!internalLoading) {
      inflightRequestRef.current = false;
    }
  }, [internalLoading]);

  return {
    displayableRates: displayableRatesState,
    rawRatesFromApi: rawRatesState,
    isLoading: internalLoading || ratesApiLoading,
    error: errorState,
  };
}; 